import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Jalali } from 'jalali-ts';
import { General_Interface } from 'src/app/Interface/General_Interface';
import { IC_ExecutionDetail, IC_HistoryPage, IC_HistoryRow, IC_Result, IC_TemplateItem } from 'src/app/Interface/IC_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { DeviceGroupService } from 'src/app/Service/DeviceGroup/DeviceGroup.service';
import { ICService } from 'src/app/Service/IC/IC.service';
import * as XLSX from 'xlsx';

interface IC_HistoryAttachment { id: number; file_Name: string; file_Type: string; file_Size?: number; }

@Component({ selector: 'app-ic-execution-history', templateUrl: './ICExecutionHistory.component.html', styleUrls: ['./ICExecutionHistory.component.css'] })
export class ICExecutionHistoryComponent implements OnInit {
  readonly dashboardScopeNotice = 'محدوده قالب، تجهیز یا بازه داشبورد فعال است؛ برای خروج از این محدوده، فیلترها را پاک کنید.';
  rows: IC_HistoryRow[] = [];
  GroupList: General_Interface[] = [];
  FilterForm: FormGroup = this.fb.group({ templateTitle: [''], templateCode: [''], equipmentKeyword: [''],
    equipmentCode: [''], groupId: [0], itemTitle: [''], result: [''], version: [''], from: [''], to: [''] });
  private appliedFilters: any = {};
  dashboardScope: any = {};
  equipmentId = 0;
  loading = false;
  page = 1;
  pageSize = 50;
  total = 0;
  summary = { total: 0, passed: 0, failed: 0, pending: 0 };
  detailRow?: IC_HistoryRow;
  detail?: IC_ExecutionDetail;
  attachments: IC_HistoryAttachment[] = [];
  detailLoading = false;
  attachmentError = '';
  dateError = '';

  constructor(private route: ActivatedRoute, private fb: FormBuilder, private service: ICService,
    private groupService: DeviceGroupService, private alert: AlertifyService) { }

  ngOnInit(): void {
    this.equipmentId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    const result = this.route.snapshot.queryParamMap?.get('result');
    if (result) this.FilterForm.patchValue({ result });
    const query = this.route.snapshot.queryParamMap;
    const groupId = Number(query?.get('groupId')) || 0;
    if (groupId) this.FilterForm.patchValue({ groupId });
    const templateId = Number(query?.get('templateId')) || 0;
    if (templateId) this.dashboardScope.templateId = templateId;
    const keyword = query?.get('dashboardEquipmentKeyword');
    if (keyword) this.dashboardScope.dashboardEquipmentKeyword = keyword;
    const scopeWindow = query?.get('scopeWindow');
    if (scopeWindow) this.dashboardScope.scopeWindow = scopeWindow;
    const executionId = Number(this.route.snapshot.queryParamMap?.get('executionId')) || 0;
    if (executionId) this.openDetail({ e: { id: executionId } as any, itemCount: 0 } as IC_HistoryRow);
    this.groupService.GetDeviceGroup(0).subscribe({
      next: data => this.GroupList = data || [],
      error: () => this.alert.ErrorMessage('دریافت گروه‌های تجهیز ناموفق بود.')
    });
    this.search();
  }

  search(): void {
    const filters = this.FilterForm.getRawValue();
    const from = this.toGregorian(filters.from);
    const to = this.toGregorian(filters.to);
    if ((filters.from && !from) || (filters.to && !to)) {
      this.dateError = 'تاریخ شمسی واردشده معتبر نیست.';
      return;
    }
    if (from && to && from > to) {
      this.dateError = 'تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد.';
      return;
    }
    this.dateError = '';
    this.appliedFilters = { ...filters, ...this.dashboardScope, from, to };
    this.page = 1;
    this.load();
  }

  clear(): void {
    this.dashboardScope = {};
    this.FilterForm.reset({ templateTitle: '', templateCode: '', equipmentKeyword: '', equipmentCode: '',
      groupId: 0, itemTitle: '', result: '', version: '', from: '', to: '' });
    this.search();
  }

  private toGregorian(value: string): string | undefined {
    if (!value || !value.trim()) return undefined;
    const digits = value.trim().replace(/[۰-۹]/g, character => String(character.charCodeAt(0) - 0x06F0))
      .replace(/[٠-٩]/g, character => String(character.charCodeAt(0) - 0x0660)).replace(/\//g, '-');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(digits)) return undefined;
    try {
      const parsed = Jalali.parse(digits);
      return parsed.format('YYYY-MM-DD') === digits ? parsed.gregorian('YYYY-MM-DD') : undefined;
    } catch { return undefined; }
  }

  formatPersianDate(value?: string | null): string {
    if (!value) return '-';
    try {
      const date = Jalali.gregorian(value);
      return date.format(/T\d{2}:\d{2}| \d{2}:\d{2}/.test(value) ? 'YYYY/MM/DD HH:mm:ss' : 'YYYY/MM/DD');
    } catch { return '-'; }
  }

  changePage(page: number): void { this.page = page; this.load(); }

  private load(): void {
    this.loading = true;
    const filters = { ...this.appliedFilters, equipmentId: this.equipmentId || undefined,
      groupId: Number(this.appliedFilters.groupId) || undefined,
      version: Number(this.appliedFilters.version) || undefined, page: this.page, pageSize: this.pageSize };
    this.service.getHistoryPage(filters).pipe(finalize(() => this.loading = false)).subscribe({
      next: (data: IC_HistoryPage) => {
        this.rows = data.items || [];
        this.total = data.total || 0;
        this.summary = data.summary || { total: this.total, passed: 0, failed: 0, pending: 0 };
      },
      error: () => this.alert.ErrorMessage('دریافت تاریخچه کنترل میانی ناموفق بود.')
    });
  }

  openDetail(row: IC_HistoryRow): void {
    this.detailRow = row;
    this.detail = undefined;
    this.attachments = [];
    this.attachmentError = '';
    this.detailLoading = true;
    forkJoin({
      detail: this.service.getExecution(row.e.id),
      attachments: this.service.getAttachments(row.e.id).pipe(catchError(() => {
        this.attachmentError = 'دریافت فایل‌های پیوست ناموفق بود.';
        return of([]);
      }))
    }).pipe(finalize(() => this.detailLoading = false)).subscribe({
      next: data => { this.detail = data.detail; this.attachments = data.attachments || []; },
      error: () => this.alert.ErrorMessage('دریافت جزئیات اجرا ناموفق بود.')
    });
  }

  closeDetail(): void { this.detailRow = undefined; this.detail = undefined; this.attachments = []; }

  itemFor(result: IC_Result): IC_TemplateItem | undefined {
    return this.detail?.items.find(item => item.id === result.iC_Template_Item_ID);
  }

  resultLabel(value?: string | null): string { return value || 'در انتظار'; }
  resultClass(value?: string | null): string {
    return value === 'PASS' ? 'badge-success' : value === 'FAIL' ? 'badge-danger' : 'badge-secondary';
  }
  value(value: unknown): string { return value === null || value === undefined || value === '' ? '-' : String(value); }
  attachmentType(value: string): string {
    return ({ PHOTO: 'تصویر', PDF: 'PDF', EXCEL: 'Excel', RAW_DATA: 'داده خام',
      CALIBRATION_CERTIFICATE: 'گواهی کالیبراسیون' } as any)[value] || value || '-';
  }
  canPreview(file: IC_HistoryAttachment): boolean { return /\.(png|jpe?g|webp|pdf)$/i.test(file.file_Name || ''); }

  preview(file: IC_HistoryAttachment): void {
    if (!this.canPreview(file)) return;
    this.service.downloadAttachment(file.id).subscribe({
      next: blob => {
        const extension = (file.file_Name || '').split('.').pop()?.toLowerCase();
        const type = extension === 'pdf' ? 'application/pdf' : extension === 'png' ? 'image/png' :
          extension === 'webp' ? 'image/webp' : 'image/jpeg';
        const url = URL.createObjectURL(new Blob([blob], { type }));
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }, error: () => this.alert.ErrorMessage('مشاهده فایل ناموفق بود.')
    });
  }

  download(file: IC_HistoryAttachment): void {
    this.service.downloadAttachment(file.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url; link.download = file.file_Name || 'attachment'; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }, error: () => this.alert.ErrorMessage('دانلود فایل ناموفق بود.')
    });
  }

  exportExcel(): void {
    const data = this.rows.map(row => ({ 'تاریخ': this.formatPersianDate(row.e.execution_Date), 'تجهیز': row.equipmentName,
      'کد تجهیز': row.equipmentCode, 'گروه': row.group, 'قالب': row.iC_Title, 'کد قالب': row.iC_Code,
      'آیتم': row.itemTitle, 'نسخه': row.e.template_Version, 'نتیجه': this.resultLabel(row.e.final_Result) }));
    const ws = XLSX.utils.json_to_sheet(data); const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'IC History');
    XLSX.writeFile(wb, 'IC-Execution-History.xlsx');
  }
  print(): void { window.print(); }
}
